import React, { useEffect, useState, useCallback } from "react";
import { spacing, fontSizes, buttons, rounded } from "../styles/tokens";
import { YT_API_KEY } from "../firebase";
import { IconCornerDownLeft } from "@tabler/icons-react";

// Parse ISO 8601 duration (e.g., PT4M13S → "4:13")
function formatISODuration(iso) {
  const match = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/.exec(iso) || [];
  const h = parseInt(match[1] || "0", 10);
  const m = parseInt(match[2] || "0", 10);
  const s = parseInt(match[3] || "0", 10);
  const total = h * 3600 + m * 60 + s;
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return h > 0
    ? `${h}:${String(mm % 60).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
    : `${mm}:${String(ss).padStart(2, "0")}`;
}

// Session cache for queries
const queryCache = new Map(); // key: query string, value: array of results

/**
 * Props:
 * - onSelect: (video: { id, title }) => void
 * - initialQuery?: string
 * - regionCode?: string (default "GB")
 * - safeSearch?: "none" | "moderate" | "strict" (default "moderate")
 * - maxResults?: number (default 15)
 * - resultsMaxHeightClass?: string (default "max-h-72")
 * - clearOnSelect?: boolean (default true)
 */
export default function YouTubeSearch({
  onSelect,
  initialQuery = "",
  regionCode = "GB",
  safeSearch = "moderate",
  maxResults = 15,
  resultsMaxHeightClass = "max-h-72",
  clearOnSelect = true,
}) {
  const apiKey = YT_API_KEY || "";
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");

  // Debounce q
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 450);
    return () => clearTimeout(t);
  }, [q]);

  const runSearch = useCallback(
    async (query) => {
      const trimmed = (query || "").trim();
      if (!apiKey) {
        setErrorMsg("Missing YouTube API key. Add YT_API_KEY to src/firebase.js.");
        return;
      }
      if (trimmed.length < 3) {
        setResults([]);
        setErrorMsg("");
        return;
      }

      const cacheKey = `${trimmed}::${maxResults}::${regionCode}::${safeSearch}`;
      if (queryCache.has(cacheKey)) {
        setResults(queryCache.get(cacheKey));
        setErrorMsg("");
        return;
      }

      setLoading(true);
      setErrorMsg("");

      try {
        // 1) search.list
        const searchUrl = new URL("https://www.googleapis.com/youtube/v3/search");
        searchUrl.searchParams.set("part", "snippet");
        searchUrl.searchParams.set("type", "video");
        searchUrl.searchParams.set("maxResults", String(Math.min(Math.max(1, maxResults), 50)));
        searchUrl.searchParams.set("q", trimmed);
        searchUrl.searchParams.set("key", apiKey);
        searchUrl.searchParams.set("regionCode", regionCode);
        searchUrl.searchParams.set("safeSearch", safeSearch);

        const sRes = await fetch(searchUrl.toString());
        const sJson = await sRes.json();

        if (!sRes.ok || sJson.error) {
          throw new Error(sJson?.error?.message || "YouTube search failed.");
        }

        const ids = (sJson.items || []).map((i) => i.id?.videoId).filter(Boolean);
        if (ids.length === 0) {
          setResults([]);
          queryCache.set(cacheKey, []);
          setLoading(false);
          return;
        }

        // 2) videos.list (batched)
        const videosUrl = new URL("https://www.googleapis.com/youtube/v3/videos");
        videosUrl.searchParams.set("part", "contentDetails,snippet,statistics");
        videosUrl.searchParams.set("id", ids.join(","));
        videosUrl.searchParams.set("key", apiKey);

        const vRes = await fetch(videosUrl.toString());
        const vJson = await vRes.json();
        if (!vRes.ok || vJson.error) {
          throw new Error(vJson?.error?.message || "YouTube details fetch failed.");
        }

        const byId = new Map();
        (vJson.items || []).forEach((v) => byId.set(v.id, v));

        const final = ids
          .map((id) => byId.get(id))
          .filter(Boolean)
          .map((v) => ({
            id: v.id,
            title: v.snippet?.title || "Untitled",
            channelTitle: v.snippet?.channelTitle || "Unknown channel",
            duration: formatISODuration(v.contentDetails?.duration || "PT0S"),
            thumbnail:
              v.snippet?.thumbnails?.medium?.url ||
              v.snippet?.thumbnails?.default?.url ||
              "",
          }));

        setResults(final);
        queryCache.set(cacheKey, final);
      } catch (e) {
        setErrorMsg(e.message || "Search failed.");
      } finally {
        setLoading(false);
      }
    },
    [apiKey, regionCode, safeSearch, maxResults]
  );

  // Debounced search trigger
  useEffect(() => {
    if (debouncedQ && debouncedQ.length >= 3 && debouncedQ === q) {
      runSearch(debouncedQ);
    }
  }, [debouncedQ, q, runSearch]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setSubmittedQ(q);
    runSearch(q);
  };

  const handleChoose = (video) => {
    onSelect(video);
    if (clearOnSelect) {
      setQ("");
      setResults([]);
      setSubmittedQ("");
      setErrorMsg("");
    }
  };

  const canSearch = (q || "").trim().length >= 3;

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className={`flex items-stretch ${spacing.gapSm}`}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search YouTube (min 3 chars)…"
          className={`flex-1 min-w-0 p-2 border ${rounded.md}`}
          aria-label="YouTube search"
        />

        {/* Submit: icon-only on mobile, label from sm+ */}
        <button
          type="submit"
          className={`${buttons.activePurple} ${rounded.md} shrink-0 px-3 sm:px-4 inline-flex items-center justify-center gap-2`}
          disabled={!canSearch || loading}
          title={!canSearch ? "Type at least 3 characters" : "Search"}
          aria-label="Search"
        >
          <IconCornerDownLeft size={18} className="sm:hidden" />
          <span className="hidden sm:inline">{loading ? "Searching…" : "Search"}</span>
        </button>

        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setResults([]);
              setErrorMsg("");
              setSubmittedQ("");
            }}
            className={`${buttons.whitePurple} ${buttons.purpleHover} ${rounded.md} shrink-0 px-3 sm:px-4`}
            title="Clear"
            aria-label="Clear search"
          >
            <span className="hidden sm:inline">Clear</span>
            <span className="sm:hidden">✕</span>
          </button>
        )}
      </form>

      {errorMsg && <div className={`mt-2 text-red-600 ${fontSizes.sm}`}>{errorMsg}</div>}

      {/* Scrollable results container */}
      {results.length > 0 && (
        <div className={`mt-3 space-y-2 overflow-y-auto ${resultsMaxHeightClass} pr-1`}>
          {results.slice(0, maxResults).map((r) => (
            <button
              key={r.id}
              onClick={() => handleChoose({ id: r.id, title: r.title })}
              className="w-full text-left"
              title="Load this video"
            >
              <div className="flex items-center p-2 border rounded-md gap-3 hover:bg-gray-50 active:bg-gray-100 transition">
                {r.thumbnail ? (
                  <img src={r.thumbnail} alt={r.title} className="w-24 h-16 object-cover rounded" />
                ) : (
                  <div className="w-24 h-16 bg-gray-200 rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{r.title}</div>
                  <div className={`truncate ${fontSizes.sm} text-gray-600`}>{r.channelTitle}</div>
                  <div className={`${fontSizes.sm}`}>⏱ {r.duration}</div>
                </div>
                <div className="shrink-0 opacity-60">
                  <IconCornerDownLeft size={18} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading && submittedQ && results.length === 0 && !errorMsg && (
        <div className={`${fontSizes.sm} text-gray-600 mt-2`}>No results.</div>
      )}
    </div>
  );
}
